
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Wrench, Square, RectangleHorizontal, ShoppingCart, Truck, Construction, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { services } from '@/data/services';

const iconMap = {
  Windshield: Wrench,
  Square: Square,
  RectangleHorizontal: RectangleHorizontal,
  ShoppingCart: ShoppingCart,
  Truck: Truck,
  Construction: Construction,
  Sun: Sun,
  Wrench: Wrench
};

function ServiceDetailPage() {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const Icon = iconMap[service.icon] || Wrench;

  return (
    <>
      <Helmet>
        <title>{`${service.title} | avtosklo-kyiv.com.ua`}</title>
        <meta name="description" content={service.description} />
      </Helmet>

      <Header />

      <main>
        <section className="py-20 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Button variant="ghost" className="mb-8" asChild>
                <Link to="/services">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Назад до послуг
                </Link>
              </Button>

              <div className="flex items-start gap-6 mb-8">
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 text-balance" style={{ letterSpacing: '-0.02em' }}>
                    {service.title}
                  </h1>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>

              <div className="prose prose-lg max-w-none mb-8">
                <h2 className="text-2xl font-semibold mb-4">Детальний опис</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {service.detailedDescription}
                </p>

                <h2 className="text-2xl font-semibold mb-4">Переваги</h2>
                <ul className="space-y-3 mb-6">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <h2 className="text-2xl font-semibold mb-4">Вартість</h2>
                <p className="text-muted-foreground leading-relaxed mb-8">
                  {service.pricing}
                </p>
              </div>

              <div className="bg-primary text-primary-foreground rounded-2xl p-8 text-center">
                <h2 className="text-2xl font-semibold mb-4">Готові замовити послугу?</h2>
                <p className="mb-6 opacity-90">
                  Зателефонуйте нам або залиште заявку онлайн. Ми швидко відповімо та підберемо зручний час.
                </p>
                <Button size="lg" variant="secondary" asChild>
                  <Link to="/contacts">
                    Записатись
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default ServiceDetailPage;
